import { ApiMethod } from '@wajeez-web/stores/base';
import { contentClient, Server } from '@wajeez-web/core/api-base';
import { CommonActions, AlertType } from '@wajeez-web/stores/common';
import { thunkErrorHandler } from '@wajeez-web/core/utils';

export const getCollectionComponentById: ApiMethod<
  string,
  Server.CollectionComponentDetailsOutput,
  any
> = async (id, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_CollectionComponents_Client(
      undefined,
      contentClient
    );
    const response = await client.collectionComponents_byId_get_V1(undefined, id);
    if (response.success && response.data) {
      return response.data;
    }
    return rejectWithValue(response.errors);
  } catch (error: any) {
    const errors = JSON.parse(error.response).errors;

    dispatch(
      CommonActions.setAlert({
        show: true,
        type: AlertType.ERROR,
        messages: errors || [{ message: 'Something went wrong' }],
      })
    );

    return rejectWithValue(errors);
  }
};

export const getCollectionComponentByIdMultilingual: ApiMethod<
  string,
  Server.CollectionComponentMultilingualOutput,
  void
> = async (id, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      const client = new Server.Server_CollectionComponents_Client(
        undefined,
        contentClient
      );

      const response =
        await client.admin_CollectionComponents_multilingualById_get_V1(id);

      if (response.success && response.data) {
        return response.data;
      }

      return rejectWithValue(response.errors);
    },
    dispatch,
    rejectWithValue
  );
};

export const editCollectionComponent: ApiMethod<
  Server.EditCollectionComponentInput,
  string,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_CollectionComponents_Client(
      undefined,
      contentClient
    );
    const response = await client.collectionComponents_put_V1({
      ...data,
      languageId: 1,
    });

    if (response.success && response.data) {
      return response.data;
    }

    return rejectWithValue(response.errors);
  } catch (error: any) {
    const errors = JSON.parse(error.response).errors;

    dispatch(
      CommonActions.setAlert({
        show: true,
        type: AlertType.ERROR,
        messages: errors || [{ message: 'Something went wrong' }],
      })
    );

    return rejectWithValue(errors);
  }
};

export const editCollectionComponentMultilingual: ApiMethod<
  Server.EditCollectionComponentV2Input,
  string,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      const client = new Server.Server_CollectionComponents_Client(
        undefined,
        contentClient
      );

      const response = await client.admin_CollectionComponents_put_V2(data);

      if (response.success && response.data) {
        return response.data;
      }

      return rejectWithValue(response.errors);
    },
    dispatch,
    rejectWithValue
  );
};

export const createCollectionComponentByTypeId: ApiMethod<
  Server.CreateCollectionComponentByTypeIdInput,
  string,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_CollectionComponents_Client(
      undefined,
      contentClient
    );
    const response = await client.collectionComponents_post_V1({
      ...data,
      languageId: 1,
    });

    if (response.success && response.data) {
      return response.data;
    }

    return rejectWithValue(response.errors);
  } catch (error: any) {
    const errors = JSON.parse(error.response).errors;

    dispatch(
      CommonActions.setAlert({
        show: true,
        type: AlertType.ERROR,
        messages: errors || [{ message: 'Something went wrong' }],
      })
    );

    return rejectWithValue(errors);
  }
};

export const createCollectionComponentByTypeIdMultilingual: ApiMethod<
  Server.CreateCollectionComponentByTypeIdV2Input,
  string,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      const client = new Server.Server_CollectionComponents_Client(
        undefined,
        contentClient
      );

      const response = await client.admin_CollectionComponents_post_V2(data);

      if (response.success && response.data) {
        return response.data;
      }

      return rejectWithValue(response.errors);
    },
    dispatch,
    rejectWithValue
  );
};

export const addComponentToCollectionComponent: ApiMethod<
  Server.AddComponentToDynamicZoneInput,
  void,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_CollectionComponents_Client(
      undefined,
      contentClient
    );

    const response = await client.collectionComponents_addToDynamicZone_post_V1(
      data
    );

    if (response.success) {
      return;
    }

    return rejectWithValue(response.errors);
  } catch (error: any) {
    const errors = JSON.parse(error.response).errors;

    dispatch(
      CommonActions.setAlert({
        show: true,
        type: AlertType.ERROR,
        messages: errors || [{ message: 'Something went wrong' }],
      })
    );

    return rejectWithValue(errors);
  }
};

export const removeComponentFromCollectionComponent: ApiMethod<
  Server.RemoveComponentFromDynamicZoneInput,
  void,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_CollectionComponents_Client(
      undefined,
      contentClient
    );

    const response =
      await client.collectionComponents_removeFromDynamicZone_post_V1(data);

    if (response.success) {
      return;
    }

    return rejectWithValue(response.errors);
  } catch (error: any) {
    const errors = JSON.parse(error.response).errors;

    dispatch(
      CommonActions.setAlert({
        show: true,
        type: AlertType.ERROR,
        messages: errors || [{ message: 'Something went wrong' }],
      })
    );

    return rejectWithValue(errors);
  }
};

export const reOrderComponentsInCollectionComponent: ApiMethod<
  Server.ReArrangeDynamicZoneOrderInput,
  void,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_CollectionComponents_Client(
      undefined,
      contentClient
    );

    const response =
      await client.collectionComponents_reArrangeDynamicZone_post_V1(data);

    if (response.success) {
      return;
    }

    return rejectWithValue(response.errors);
  } catch (error: any) {
    const errors = JSON.parse(error.response).errors;

    dispatch(
      CommonActions.setAlert({
        show: true,
        type: AlertType.ERROR,
        messages: errors || [{ message: 'Something went wrong' }],
      })
    );

    return rejectWithValue(errors);
  }
};

export const getCollectionComponentDatatable: ApiMethod<
  Server.CollectionComponentLookUpDatatable,
  Server.TableOfCollectionComponentLookUpColumns,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_CollectionComponents_Client(
      undefined,
      contentClient
    );

    const response =
      await client.collectionComponents_lookups_datatable_post_V1(data);

    if (response.success && response.data) {
      return response.data;
    }

    return rejectWithValue(response.errors);
  } catch (error: any) {
    const errors = JSON.parse(error.response).errors;

    dispatch(
      CommonActions.setAlert({
        show: true,
        type: AlertType.ERROR,
        messages: errors || [{ message: 'Something went wrong' }],
      })
    );

    return rejectWithValue(errors);
  }
};

export const getCollectionComponentLookups: ApiMethod<
  { searchText?: string },
  Server.CollectionComponentLookUpOutput[],
  void
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_CollectionComponents_Client(
      undefined,
      contentClient
    );

    const response = await client.collectionComponents_lookups_get_V1(
      undefined,
      data.searchText
    );

    if (response.success && response.data) {
      return response.data;
    }

    return rejectWithValue(response.errors);
  } catch (error: any) {
    const errors = JSON.parse(error.response).errors;

    dispatch(
      CommonActions.setAlert({
        show: true,
        type: AlertType.ERROR,
        messages: errors || [{ message: 'Something went wrong' }],
      })
    );

    return rejectWithValue(errors);
  }
};

export const getCollectionComponentsStandAloneLookups: ApiMethod<
  { searchText?: string },
  Server.CollectionComponentLookUpOutput[],
  void
> = async (data, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      const client = new Server.Server_CollectionComponents_Client(
        undefined,
        contentClient
      );

      const response = await client.collectionComponents_lookups_get_V1(
        undefined,
        data.searchText,
        Server.CollectionComponentUsageType.StandAlone
      );

      if (response.success && response.data) {
        return response.data;
      }

      return rejectWithValue(response.errors);
    },
    dispatch,
    rejectWithValue
  );
};

export const getCollectionComponentLayoutLookups: ApiMethod<
  { searchText?: string },
  Server.CollectionComponentLookUpOutput[],
  void
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_CollectionComponents_Client(
      undefined,
      contentClient
    );

    const response = await client.collectionComponents_lookups_get_V1(
      undefined,
      data.searchText,
      Server.CollectionComponentUsageType.Layout
    );

    if (response.success && response.data) {
      return response.data;
    }

    return rejectWithValue(response.errors);
  } catch (error: any) {
    const errors = JSON.parse(error.response).errors;

    dispatch(
      CommonActions.setAlert({
        show: true,
        type: AlertType.ERROR,
        messages: errors || [{ message: 'Something went wrong' }],
      })
    );

    return rejectWithValue(errors);
  }
};

export const getCollectionComponentBodyLookups: ApiMethod<
  { searchText?: string },
  Server.CollectionComponentLookUpOutput[],
  void
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_CollectionComponents_Client(
      undefined,
      contentClient
    );

    const response = await client.collectionComponents_lookups_get_V1(
      undefined,
      data.searchText,
      Server.CollectionComponentUsageType.Body
    );

    if (response.success && response.data) {
      return response.data;
    }

    return rejectWithValue(response.errors);
  } catch (error: any) {
    const errors = JSON.parse(error.response).errors;

    dispatch(
      CommonActions.setAlert({
        show: true,
        type: AlertType.ERROR,
        messages: errors || [{ message: 'Something went wrong' }],
      })
    );

    return rejectWithValue(errors);
  }
};

export const setCollectionComponentDynamicZone: ApiMethod<
  {
    dynamicZoneContainerId: string;
    componentIds: string[];
  },
  void,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_CollectionComponents_Client(
      undefined,
      contentClient
    );

    const response = await client.collectionComponents_setDynamicZone_post_V1(
      data
    );

    if (response.success) {
      return;
    }

    return rejectWithValue(response.errors);
  } catch (error: any) {
    const errors = JSON.parse(error.response).errors;

    dispatch(
      CommonActions.setAlert({
        show: true,
        type: AlertType.ERROR,
        messages: errors || [{ message: 'Something went wrong' }],
      })
    );

    return rejectWithValue(errors);
  }
};

export const cloneCollectionComponent: ApiMethod<
  Server.CloneCollectionComponentInput,
  string,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_CollectionComponents_Client(
      undefined,
      contentClient
    );

    const response = await client.collectionComponents_clone_post_V1(data);

    if (response.success && response.data) {
      return response.data;
    }

    return rejectWithValue(response.errors);
  } catch (error: any) {
    const errors = JSON.parse(error.response).errors;

    dispatch(
      CommonActions.setAlert({
        show: true,
        type: AlertType.ERROR,
        messages: errors || [{ message: 'Something went wrong' }],
      })
    );

    return rejectWithValue(errors);
  }
};
